// ═══════════════════════════════════════════════════════════════════════════════
// LogoLibraryPicker — pick a preset SVG mark from the platform library
// and apply it to the tenant's branding. Tenant can still override with
// their own upload afterwards — this is additive, not replacing.
//
// Picking a preset writes:
//   • logo_url       → /logos/<slug>.svg          (black-stroke variant)
//   • logo_dark_url  → /logos/<slug>-dark.svg     (white-stroke variant)
//   • favicon_url    → /logos/<slug>-favicon.svg  (THEME-ADAPTIVE — uses a
//                      prefers-color-scheme media query inside the SVG so
//                      strokes auto-flip black ↔ white based on the browser
//                      tab strip color. Chrome / Safari / Edge default to
//                      dark tabs; Firefox + light-themed Chrome use light.
//                      A single-color favicon is invisible on the matching
//                      background, so the adaptive variant is the only one
//                      that stays visible across the full browser matrix.)
//
// The currently-selected preset (if any) is highlighted by matching
// the tenant's logo_url against the library URLs. Tenants whose logo
// was uploaded directly (not picked from the library) see no
// highlighted card — they can still click to overwrite.
// ═══════════════════════════════════════════════════════════════════════════════

import { Sparkles, Check } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { LOGO_LIBRARY, type LogoLibraryItem } from './logoLibrary';

interface Props {
  /** Current logo_url. Used to highlight the active preset (if any). */
  logoUrl:        string;
  /** Setter: called with patch (logo_url, logo_dark_url, favicon_url)
   *  so the parent form state stays in sync. */
  onPick:         (patch: { logo_url: string; logo_dark_url: string; favicon_url: string }) => void;
}

export function LogoLibraryPicker({ logoUrl, onPick }: Props) {
  const currentSlug = LOGO_LIBRARY.find(item => item.lightUrl === logoUrl)?.slug ?? null;

  const handlePick = (item: LogoLibraryItem) => {
    onPick({
      logo_url:      item.lightUrl,
      logo_dark_url: item.darkUrl,
      // Favicon uses the ADAPTIVE variant — see header comment.
      // The SVG itself embeds a prefers-color-scheme media query
      // so it renders black on light tab strips (Firefox / light
      // Chrome) and white on dark tab strips (Chrome / Safari /
      // Edge default). One file, visible everywhere — no
      // per-browser overrides needed.
      favicon_url:   item.faviconUrl,
    });
  };

  return (
    <div className="glass-panel p-5 mb-4">
      <div className="flex items-start gap-3 mb-4">
        <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-surface-900">Pick from the logo library</h3>
          <p className="text-xs text-surface-500 mt-0.5">
            One-click brand marks across travel themes — light and dark variants set automatically.
            You can still upload your own afterwards if none of these fit.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {LOGO_LIBRARY.map(item => {
          const isActive = item.slug === currentSlug;
          return (
            <button
              key={item.slug}
              onClick={() => handlePick(item)}
              className={cn(
                'group relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-left',
                isActive
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/15'
                  : 'border-surface-200 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10',
              )}
              title={item.description}
            >
              {/* Active checkmark */}
              {isActive && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-500 text-white flex items-center justify-center">
                  <Check size={12} strokeWidth={3} />
                </div>
              )}

              {/* Preview: shows the actual SVG file the picker will set. */}
              <div className="w-full flex items-center justify-center gap-2 py-2">
                <div className="rounded-md bg-white p-1.5 border border-surface-200">
                  <img src={item.lightUrl} alt={item.name} className="w-10 h-10 object-contain" />
                </div>
                <div className="rounded-md bg-slate-900 p-1.5">
                  <img src={item.darkUrl} alt={`${item.name} dark`} className="w-10 h-10 object-contain" />
                </div>
              </div>

              <div className="w-full">
                <div className="text-xs font-semibold text-surface-900 truncate">{item.name}</div>
                <div className="text-[11px] text-surface-500 line-clamp-2 leading-snug mt-0.5">
                  {item.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
